import { HISTORY_ACTION_MODE, NodeId } from '@candulabs/craft';
import { useCallback, useMemo } from 'react';

import { useMultiNodes, useResponsiveCss, useResponsiveProps } from 'craft/hooks';
import { useComponentSchema } from 'craft/state';
import { CssSelectorId } from 'craft/types';

import { getSizeValues, setSizeValues } from './sizeValues';
import { Size } from './types';

interface UseSize {
  size: Size;
  setSize: (size: Size, historyMode?: HISTORY_ACTION_MODE, throttleNumber?: number) => void;
}

export const useSize = (nodeIds: NodeId[], cssSelectorId?: CssSelectorId): UseSize => {
  const { responsiveProps, setResponsiveProps } = useResponsiveProps(nodeIds);
  const { responsiveCss, setResponsiveCss } = useResponsiveCss(nodeIds, undefined, cssSelectorId);
  const { type } = useMultiNodes(nodeIds);

  const componentSchema = useComponentSchema(type);
  const nodePropsArray = componentSchema?.props?.map((prop) => prop.id) || [];
  const entriesProperties = ['width', 'height'].map((key) => [
    key,
    (responsiveProps ?? {})[key] || (responsiveCss ?? {})[key] || undefined,
  ]);

  const size = useMemo(() => getSizeValues(Object.fromEntries(entriesProperties)), [
    entriesProperties,
  ]);

  const setSize = useCallback(
    (
      updatedSize: Size,
      historyMode?: HISTORY_ACTION_MODE,
      throttleNumber?: number,
      property: 'min' | 'max' | 'normal' = 'normal',
    ) => {
      const sizeObjectValues = setSizeValues(updatedSize);

      const values =
        property === 'normal'
          ? sizeObjectValues
          : {
              [`${property}Width`]: sizeObjectValues.width,
              [`${property}Height`]: sizeObjectValues.height,
            };

      const shouldSetProp = Object.keys(values).every(
        (sizeProp) => nodePropsArray.indexOf(sizeProp) > -1,
      );

      if (shouldSetProp) {
        setResponsiveProps(values, historyMode, throttleNumber);
      } else {
        setResponsiveCss(values, historyMode, throttleNumber);
      }
    },
    [nodePropsArray, setResponsiveProps, setResponsiveCss],
  );

  return {
    size,
    setSize,
  };
};
